import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Checkbox, Typography, Icon, LoadingButton } from '@eventbrite/marmalade';
import { Modal } from '@/components/_shared/Modal/Modal';
import { redirectToSignIn } from '@/utils/utils';
import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';
import { OrganizerInfo } from '@/components/EventDetails/OrganizerInfo';
import styles from './RemindMe.module.scss';
import { addOrganizationMarketingOptIn, unremind, remind, getReminderStatus } from './remindMeClient';
import { RemindMeIcon } from './RemindMeIcon';
import { useUserData } from '@/hooks/useUserData';
import { ReminderSetIcon } from '@/components/_shared/Icons/ReminderSetIcon';

const CONSENT_MESSAGE = {
  id: 'Keep me updated with news and offers from the organizer.',
  defaultMessage: 'Keep me updated with news and offers from the organizer.',
};

const getConsentStorageKey = (eventId: string) => `listings:remind-me:marketing-opt-in:${eventId}`;

const isAuthReturnRequested = () => new URLSearchParams(window.location.search).get('remindMe') === 'true';

const readStoredConsent = (eventId: string): boolean => {
  try {
    return window.sessionStorage.getItem(getConsentStorageKey(eventId)) === 'true';
  } catch {
    return false;
  }
};

const storeConsent = (eventId: string, checked: boolean) => {
  try {
    window.sessionStorage.setItem(getConsentStorageKey(eventId), String(checked));
  } catch (error) {
    console.warn('Unable to preserve reminder marketing consent for sign-in:', error);
  }
};

const clearAuthReturnState = (eventId: string) => {
  try {
    window.sessionStorage.removeItem(getConsentStorageKey(eventId));
  } catch (error) {
    console.warn('Unable to clear reminder marketing consent after sign-in:', error);
  }

  const url = new URL(window.location.href);
  url.searchParams.delete('remindMe');
  window.history.replaceState(null, '', url.toString());
};

type AuthReturnResult = {
  shouldOptIn: boolean;
  claimMarketingOptIn: () => boolean;
};

const authReturnRequests = new Map<string, Promise<AuthReturnResult>>();

const getOrCreateAuthReturnRequest = (eventId: string, shouldOptIn: boolean): Promise<AuthReturnResult> => {
  const existingRequest = authReturnRequests.get(eventId);
  if (existingRequest) return existingRequest;

  let marketingOptInClaimed = false;
  const request = remind(eventId).then(() => ({
    shouldOptIn,
    claimMarketingOptIn: () => {
      if (marketingOptInClaimed) return false;
      marketingOptInClaimed = true;
      return true;
    },
  }));

  authReturnRequests.set(eventId, request);
  const clearRequest = () => {
    clearAuthReturnState(eventId);
    if (authReturnRequests.get(eventId) === request) {
      authReturnRequests.delete(eventId);
    }
  };
  void request.then(clearRequest, clearRequest);

  return request;
};

type RemindMeProps = {
  secondaryAction?: ReactNode;
};

export const RemindMe: React.FC<RemindMeProps> = ({ secondaryAction }) => {
  const [isReminded, setIsReminded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasResolvedReminderStatus, setHasResolvedReminderStatus] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const consentWasChanged = useRef(false);
  const authReturnWasHandled = useRef(false);
  const { formatMessage } = useIntl();
  const { organizer, id, organizationId } = useBasicInfo();
  const { userId, countryCode, marketingContact, isLoading: isUserDataLoading } = useUserData();
  const isAuthenticated = !!userId;

  useEffect(() => {
    if (!isUserDataLoading && !consentWasChanged.current) {
      setMarketingOptIn(countryCode === 'US');
    }
  }, [countryCode, isUserDataLoading]);

  const requestMarketingOptIn = useCallback(
    (shouldOptIn: boolean) => {
      if (!shouldOptIn) return;
      if (!organizationId || !marketingContact) {
        console.warn('Skipping organizer marketing opt-in because contact or organization data is missing');
        return;
      }

      void addOrganizationMarketingOptIn({
        organizationId,
        firstName: marketingContact.firstName,
        lastName: marketingContact.lastName,
        email: marketingContact.email,
      }).catch((error) => {
        console.error('Organizer marketing opt-in failed after reminder was set:', error);
      });
    },
    [marketingContact, organizationId],
  );

  const handleReminderSuccess = useCallback(() => {
    setIsReminded(true);
    setShowModal(true);
    setHasResolvedReminderStatus(true);
  }, []);

  useEffect(() => {
    if (isUserDataLoading || !isAuthenticated || authReturnWasHandled.current || !isAuthReturnRequested()) {
      return;
    }

    authReturnWasHandled.current = true;
    const shouldOptIn = readStoredConsent(id);
    consentWasChanged.current = true;
    setMarketingOptIn(shouldOptIn);
    setIsLoading(true);
    void getOrCreateAuthReturnRequest(id, shouldOptIn)
      .then((authReturnResult) => {
        handleReminderSuccess();
        if (authReturnResult.claimMarketingOptIn()) {
          requestMarketingOptIn(authReturnResult.shouldOptIn);
        }
      })
      .catch((error) => {
        console.error('Unable to set reminder after sign-in:', error);
      })
      .finally(() => {
        setIsLoading(false);
        setHasResolvedReminderStatus(true);
      });
  }, [handleReminderSuccess, id, isAuthenticated, isUserDataLoading, requestMarketingOptIn]);

  useEffect(() => {
    if (isUserDataLoading) return;
    if (!isAuthenticated || !userId) {
      setHasResolvedReminderStatus(true);
      return;
    }
    if (!id || isAuthReturnRequested()) return;

    let cancelled = false;
    setIsLoading(true);
    void getReminderStatus(userId, id)
      .then((hasReminder) => {
        if (!cancelled) setIsReminded(hasReminder);
      })
      .catch((error) => {
        console.error('Unable to load reminder status:', error);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
          setHasResolvedReminderStatus(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isUserDataLoading, userId, id]);

  const toggleRemind = async () => {
    setIsLoading(true);
    if (!isAuthenticated) {
      storeConsent(id, marketingOptIn);
      const url = new URL(window.location.href);
      url.searchParams.set('remindMe', 'true');
      url.searchParams.delete('overrideListingsOrigin');
      window.history.pushState(null, '', url.toString());
      redirectToSignIn();
      return;
    }

    try {
      if (isReminded) {
        await unremind(id);
        setIsReminded(false);
        return;
      }

      await remind(id);
      handleReminderSuccess();
      requestMarketingOptIn(marketingOptIn);
    } catch (error) {
      console.error(`Unable to ${isReminded ? 'remove' : 'set'} reminder:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const showConsent = hasResolvedReminderStatus && !isUserDataLoading && !isLoading && !isReminded;
  const consentLabel = formatMessage(CONSENT_MESSAGE);

  return (
    <div className={styles.actionGroup} data-testid="cb-cta-area">
      <div className={styles.buttonRow}>
        <LoadingButton
          isLoading={isLoading || isUserDataLoading || !hasResolvedReminderStatus}
          variant={isReminded ? 'secondary' : 'primary'}
          onClick={toggleRemind}
          aria-label={isReminded ? 'Remove reminder' : 'Set reminder'}
        >
          {isReminded ? (
            <div className={styles.reminderSetButtonVariant}>
              <ReminderSetIcon />
              <FormattedMessage id="Reminder set" defaultMessage="Reminder set" />
            </div>
          ) : (
            <FormattedMessage id="Remind me" defaultMessage="Remind me" />
          )}
        </LoadingButton>
        {secondaryAction}
      </div>
      {showConsent && (
        <div className={styles.consentRow}>
          <Checkbox
            id="organizer-marketing-opt-in"
            name="organizer-marketing-opt-in"
            value="organization_marketing_opt_in"
            size="small"
            checked={marketingOptIn}
            onChange={(checked) => {
              consentWasChanged.current = true;
              setMarketingOptIn(checked);
            }}
            aria-label={consentLabel}
          />
          <label className={styles.consentLabel} htmlFor="organizer-marketing-opt-in">
            <FormattedMessage {...CONSENT_MESSAGE} />
          </label>
        </div>
      )}
      <Modal visible={showModal} onClose={() => setShowModal(false)} type="simple">
        <div className={styles.remindMeModalContent}>
          <Icon size="medium" className={styles.remindMeIcon}>
            <RemindMeIcon />
          </Icon>
          <Typography as="h2" variant="heading-md" className={styles.reminderSetTitle}>
            <FormattedMessage id="Reminder set!" defaultMessage="Reminder set!" />
          </Typography>
          <Typography as="p" variant="body-md" className={styles.reminderSetDescription}>
            <FormattedMessage
              id="We'll send you a notification an hour before tickets go on sale. Manage your reminder on the event page."
              defaultMessage="We'll send you a notification an hour before tickets go on sale. Manage your reminder on the event page."
            />
          </Typography>
          <section
            className={styles.organizerInfoSection}
            aria-label={formatMessage({ id: 'Organizer profile', defaultMessage: 'Organizer profile' })}
          >
            <OrganizerInfo organizer={organizer} />
          </section>
        </div>
      </Modal>
    </div>
  );
};
